import {
  buildArrivalWindowFromSlots,
  resolveSiteVisitCalendarPlacement,
  slotRangeFromArrivalWindow,
} from "./siteVisitCalendarWindows";

export const SCHEDULING_INCOMPLETE_LABEL = "Scheduling incomplete — time required";
export const CALENDAR_VISITS_LOAD_ERROR =
  "Scheduled visits could not be loaded. Please refresh or contact Admin.";

export const PLANNED_ARRIVAL_WINDOW_REQUIRED =
  "A scheduled visit requires a valid start and end time.";
export const INVALID_ARRIVAL_WINDOW =
  "Visit time is invalid. End must be after start, using a supported time range.";
export const DETAIL_SCHEDULING_INCOMPLETE_LABEL =
  "Scheduling incomplete — start and end time required";
export const TIME_NOT_SET_LABEL = "Not set";
export const RESCHEDULE_START_REQUIRED = "Start time is required.";
export const RESCHEDULE_END_REQUIRED = "End time is required.";
export const RESCHEDULE_DATE_REQUIRED = "A visit date is required.";

/** Cleo/system emergency SVRs may omit a window. Staff planned workflows must not. */
export function isCleoEmergencyVisitType(visitType?: string | null): boolean {
  return String(visitType ?? "").trim() === "urgent";
}

const FREE_FORM = /^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/;

export function dragRangeToArrivalWindow(startSlot: number, endSlot: number): string {
  const min = Math.min(startSlot, endSlot);
  const max = Math.max(startSlot, endSlot);
  const built = buildArrivalWindowFromSlots(min, max);
  if (!built) {
    throw new Error(INVALID_ARRIVAL_WINDOW);
  }
  return built;
}

export function parseValidArrivalWindow(raw: string | null | undefined): string | null {
  const value = String(raw ?? "").trim();
  if (!value) return null;
  const range = slotRangeFromArrivalWindow(value);
  if (!range || range.endSlot < range.startSlot) return null;
  const free = value.match(FREE_FORM);
  if (free) {
    const startMins = Number(free[1]) * 60 + Number(free[2]);
    const endMins = Number(free[3]) * 60 + Number(free[4]);
    if (!(endMins > startMins)) return null;
  }
  return value;
}

export function requireStaffPlannedArrivalWindow(opts: {
  visitType?: string | null;
  arrivalWindow?: string | null;
}): string {
  const visitType = String(opts.visitType ?? "planned").trim() || "planned";
  if (isCleoEmergencyVisitType(visitType) || visitType !== "planned") {
    const parsed = parseValidArrivalWindow(opts.arrivalWindow);
    return parsed ?? "";
  }
  const parsed = parseValidArrivalWindow(opts.arrivalWindow);
  if (!parsed) {
    throw new Error(
      String(opts.arrivalWindow ?? "").trim() ? INVALID_ARRIVAL_WINDOW : PLANNED_ARRIVAL_WINDOW_REQUIRED,
    );
  }
  return parsed;
}

function normalizeClockTime(raw: string): string | null {
  const m = String(raw ?? "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) {
    return null;
  }
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

export function composeArrivalWindowFromClockTimes(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  const s = normalizeClockTime(String(start ?? ""));
  const e = normalizeClockTime(String(end ?? ""));
  if (!s || !e) return null;
  return parseValidArrivalWindow(`${s}-${e}`);
}

export function validateStaffRescheduleForm(input: {
  date?: string | null;
  start?: string | null;
  end?: string | null;
}):
  | { ok: true; arrivalWindow: string }
  | { ok: false; error: string; field: "date" | "start" | "end" | "range" } {
  if (!String(input.date ?? "").trim()) {
    return { ok: false, error: RESCHEDULE_DATE_REQUIRED, field: "date" };
  }
  if (!String(input.start ?? "").trim()) {
    return { ok: false, error: RESCHEDULE_START_REQUIRED, field: "start" };
  }
  if (!String(input.end ?? "").trim()) {
    return { ok: false, error: RESCHEDULE_END_REQUIRED, field: "end" };
  }
  const arrivalWindow = composeArrivalWindowFromClockTimes(input.start, input.end);
  if (!arrivalWindow) {
    return { ok: false, error: INVALID_ARRIVAL_WINDOW, field: "range" };
  }
  return { ok: true, arrivalWindow };
}

/**
 * Staff router updates: require a valid window when rescheduling an active planned SVR.
 * Unrelated edits must not clear time. Cleo `urgent` visits may omit a window.
 */
export function resolveStaffPlannedSchedulePatch(opts: {
  visitType?: string | null;
  status?: string | null;
  existingArrivalWindow?: string | null;
  incomingArrivalWindow?: string | null;
  incomingArrivalWindowProvided: boolean;
  visitDateProvided: boolean;
}): string | undefined {
  const cancelled = String(opts.status ?? "") === "cancelled";
  if (cancelled) {
    if (!opts.incomingArrivalWindowProvided) return undefined;
    return parseValidArrivalWindow(opts.incomingArrivalWindow) ?? undefined;
  }
  if (isCleoEmergencyVisitType(opts.visitType)) {
    if (!opts.incomingArrivalWindowProvided) return undefined;
    if (opts.incomingArrivalWindow == null || String(opts.incomingArrivalWindow).trim() === "") {
      return undefined;
    }
    const parsed = parseValidArrivalWindow(opts.incomingArrivalWindow);
    if (!parsed) throw new Error(INVALID_ARRIVAL_WINDOW);
    return parsed;
  }

  const isReschedule = opts.visitDateProvided || opts.incomingArrivalWindowProvided;
  if (!isReschedule) return undefined;

  if (opts.incomingArrivalWindowProvided) {
    if (opts.incomingArrivalWindow == null || String(opts.incomingArrivalWindow).trim() === "") {
      throw new Error(PLANNED_ARRIVAL_WINDOW_REQUIRED);
    }
    return requireStaffPlannedArrivalWindow({
      visitType: "planned",
      arrivalWindow: opts.incomingArrivalWindow,
    });
  }

  return requireStaffPlannedArrivalWindow({
    visitType: "planned",
    arrivalWindow: opts.existingArrivalWindow,
  });
}

export function visitHasSchedulableTime(visit: {
  visitDate: Date | string;
  arrivalWindow?: string | null;
  arrivedAt?: Date | string | null;
  departedAt?: Date | string | null;
}): boolean {
  return resolveSiteVisitCalendarPlacement(visit) != null;
}

export function partitionCalendarVisits<T extends {
  visitDate: Date | string;
  arrivalWindow?: string | null;
  arrivedAt?: Date | string | null;
  departedAt?: Date | string | null;
}>(visits: T[] | null | undefined): { timed: T[]; incomplete: T[] } {
  const timed: T[] = [];
  const incomplete: T[] = [];
  for (const visit of Array.isArray(visits) ? visits : []) {
    if (visitHasSchedulableTime(visit)) timed.push(visit);
    else incomplete.push(visit);
  }
  return { timed, incomplete };
}

export const AUG19_2026_SCHEDULED_VISITS = [
  { visitNumber: "CLA-SVR-2026-0348", technicianName: "Sam Lim", arrivalWindow: "10:30-15:30" },
  { visitNumber: "CLA-SVR-2026-0361", technicianName: "Patrick Abad", arrivalWindow: "15:00-16:00" },
  { visitNumber: "CLA-SVR-2026-0377", technicianName: "Zwe", arrivalWindow: "10:00-12:30" },
  { visitNumber: "CLA-SVR-2026-0378", technicianName: "Zwe", arrivalWindow: "13:00-15:30" },
] as const;

export const SVR_DUPLICATE_ATTEMPTS_379_383 = [
  "CLA-SVR-2026-0379",
  "CLA-SVR-2026-0380",
  "CLA-SVR-2026-0381",
  "CLA-SVR-2026-0382",
  "CLA-SVR-2026-0383",
] as const;

export function schemaErrorLogFields(error: unknown): Record<string, unknown> {
  const asObj = (value: unknown): Record<string, unknown> | null =>
    typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
  const root = asObj(error);
  const cause = asObj(root?.cause);
  const original = asObj(root?.originalError);
  return {
    message: String(root?.message ?? cause?.message ?? original?.message ?? error ?? ""),
    code: String(root?.code ?? cause?.code ?? original?.code ?? ""),
    errno: String(root?.errno ?? cause?.errno ?? original?.errno ?? ""),
    sqlState: String(root?.sqlState ?? cause?.sqlState ?? original?.sqlState ?? ""),
    sqlMessage: String(root?.sqlMessage ?? cause?.sqlMessage ?? original?.sqlMessage ?? ""),
  };
}
